/**
 * Pure rules behind the meaning chooser (DESIGN_SYSTEM.md §14). Kept out of the
 * component so the two judgements it makes about text — how much of a sense's
 * definition identifies it, and whether a translation came from the link —
 * can be tested without rendering a card.
 */

/**
 * The part of a sense's definition that tells it apart from its neighbours.
 *
 * Dictionary definitions put the distinguishing clause first and qualify it
 * after a semicolon ("move at a speed faster than a walk; never having both
 * feet on the ground at the same time"). §14 trims a long sense to that clause
 * so a list of twelve stays a list, and leaves the full wording to the card
 * once linked. A definition with no clause break is returned whole — the
 * visual clamp is what bounds it then, not a guess at where to cut.
 */
export function glossOf(definition: string | null | undefined): string {
  const text = (definition ?? '').trim()
  if (!text) return ''
  const cut = text.search(/;\s/)
  return cut > 0 ? text.slice(0, cut).trim() : text
}

/** Where the card's authoritative Translation comes from (§14 item 7). */
export type TranslationProvenance = 'link' | 'typed'

/**
 * `link` when the field says exactly what the linked sense's translation says,
 * `typed` when it says something else, `null` when the field is empty — an
 * empty field states no provenance, because there is no text to own.
 *
 * Content equality rather than a stored flag: a side does not record how it
 * was written, and the one fact that matters to a reader deciding whether to
 * re-link is whether this text is the meaning's or not. Whitespace is ignored
 * at both ends, the same emptiness rule the editor applies everywhere else.
 */
export function translationProvenance(
  content: string, linkedTranslation: string | null | undefined,
): TranslationProvenance | null {
  const own = content.trim()
  if (!own) return null
  const linked = (linkedTranslation ?? '').trim()
  return linked && own === linked ? 'link' : 'typed'
}

/** Width of the popover when it opens beside the pane (desktop). */
export const MEANING_POPOVER_BESIDE_WIDTH = 340
/** §14: the list itself caps here and scrolls; head and foot sit outside it. */
export const MEANING_LIST_MAX_HEIGHT = 320
/** Clearance between the popover and whatever edge it is placed against. */
export const MEANING_POPOVER_GAP = 12
/** Nearest the popover may come to the viewport's own edge. */
export const MEANING_VIEWPORT_MARGIN = 8

export interface Rect { left: number; top: number; right: number; bottom: number; width: number }

export type MeaningPlacement =
  | { mode: 'beside' | 'below'; left: number; top: number; width: number; maxHeight: number }

/**
 * Where the open list goes.
 *
 * `beside` is the desktop answer to the question §14 flagged: dropped below
 * the band, the list covers the Translation and Example fields — two of the
 * three fields linking changes — at the pane's real width (616px editing,
 * 536px of band), so the reader cannot see what they are about to replace. The
 * list is therefore placed OUTSIDE the pane, to its left, over the deck list,
 * which linking does not touch. Chosen whenever that room exists; it does not
 * on a phone, where the scroller is the full screen width.
 *
 * `below` is the phone's placement and the fallback: across the band's own
 * width, starting under it. Its `maxHeight` is the whole viewport, not the
 * room under the band — a list squeezed into what is left under a band at
 * mid-screen scrolled at five senses. The component lifts it instead
 * (`liftIntoViewport`), so a long list rises over the card rather than shrinks.
 */
export function placeMeaningPopover(
  band: Rect, scroller: Rect | null, viewport: { width: number; height: number },
): MeaningPlacement {
  const m = MEANING_VIEWPORT_MARGIN
  const maxHeight = viewport.height - 2 * m
  if (scroller) {
    const right = scroller.left - MEANING_POPOVER_GAP
    const left = right - MEANING_POPOVER_BESIDE_WIDTH
    if (left >= m) {
      // Top-aligned with the band so the list reads as coming from the capsule;
      // `liftIntoViewport` raises it if the band sits too low for it to fit.
      return { mode: 'beside', left, top: Math.max(m, band.top), width: MEANING_POPOVER_BESIDE_WIDTH, maxHeight }
    }
  }
  const width = Math.min(band.width, viewport.width - 2 * m)
  const left = Math.max(m, Math.min(band.left, viewport.width - m - width))
  return { mode: 'below', left, top: band.bottom + 4, width, maxHeight }
}

/** The top that keeps a rendered popover of `height` inside the viewport:
 *  unchanged when it fits, raised by exactly the overflow when it does not,
 *  never above the margin. */
export function liftIntoViewport(top: number, height: number, viewportHeight: number): number {
  const m = MEANING_VIEWPORT_MARGIN
  const overflow = top + height - (viewportHeight - m)
  return overflow > 0 ? Math.max(m, top - overflow) : top
}
